import { useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertTriangle, Network, Pencil, Plus, Save, Trash2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  DataTable,
  EmptyState,
  ErrorState,
  FormField,
  FormGrid,
  Loading,
  Modal,
  PageHeader,
  Panel,
  StatusBadge,
  TextField,
  useConfirm,
  useToast,
  type Column,
} from '@/components';
import type { OrganizationType } from '@/auth/types';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { useBusinessOperators } from '@/features/masters/api';
import { ApiError, errorMessage } from '@/lib/api';
import { applyApiErrors } from '@/lib/form';
import { formatDateTime } from '@/lib/format';
import { cx } from '@/lib/cx';
import { useDeleteIpRule, useIpRules, useSaveIpRule, type IpRuleDto, type IpRuleInput } from './api';
import { cidrContains } from './ip';
import './security.css';

type Org = { organizationType: OrganizationType; businessOperatorId: number | null; label: string; sub?: string };
const orgKey = (o: { organizationType: string; businessOperatorId: number | null }) => `${o.organizationType}:${o.businessOperatorId ?? ''}`;

const schema = z.object({
  cidr: z
    .string()
    .trim()
    .min(1, 'IP アドレスまたは CIDR を入力してください')
    .max(64, '64文字以内で入力してください')
    .regex(/^[0-9A-Fa-f:.]+(\/\d{1,3})?$/, 'IP アドレス（例：203.0.113.10）または CIDR（例：203.0.113.0/24、2001:db8::/48）で入力してください'),
  note: z.string().trim().max(200, '200文字以内で入力してください'),
  isEnabled: z.boolean(),
});
type Values = z.infer<typeof schema>;

/** CONFIRMATION_REQUIRED（selfLockout）なら確認ダイアログの文言を返す */
function selfLockoutMessage(e: unknown): string | null {
  if (!(e instanceof ApiError) || e.code !== 'CONFIRMATION_REQUIRED') return null;
  const d = e.details as { confirmations?: string[]; message?: string } | undefined;
  return d?.confirmations?.includes('selfLockout') ? (d.message ?? e.message) : null;
}

/** S-97 IP アドレス制限（基金のみ） */
export function IpRulesPage() {
  const { year } = useFiscalYear();
  const rules = useIpRules();
  const operators = useBusinessOperators(year, { includeInactive: true });
  const [selected, setSelected] = useState<string>('基金:');
  const [editing, setEditing] = useState<IpRuleDto | 'new' | null>(null);
  const del = useDeleteIpRule();
  const confirm = useConfirm();
  const toast = useToast();

  const orgs = useMemo<Org[]>(() => {
    const base: Org[] = [
      { organizationType: '基金', businessOperatorId: null, label: '基金', sub: '振興基金' },
      { organizationType: '水産庁', businessOperatorId: null, label: '水産庁' },
      { organizationType: '沖縄県', businessOperatorId: null, label: '沖縄県' },
    ];
    for (const op of operators.data ?? []) {
      base.push({ organizationType: '漁協', businessOperatorId: op.id, label: op.name, sub: `漁協・事業実施者 ${op.id}${op.isActive ? '' : '（廃止）'}` });
    }
    return base;
  }, [operators.data]);

  const counts = useMemo(() => {
    const m = new Map<string, { total: number; enabled: number }>();
    for (const s of rules.data?.summary ?? []) m.set(orgKey(s), { total: s.total, enabled: s.enabled });
    return m;
  }, [rules.data]);

  if (rules.isPending) return <Loading block />;
  if (rules.error) return <ErrorState error={rules.error} onRetry={() => void rules.refetch()} />;
  const data = rules.data;
  const org = orgs.find((o) => orgKey(o) === selected) ?? orgs[0]!;
  const items = data.items.filter((r) => orgKey(r) === orgKey(org));
  const enabledItems = items.filter((r) => r.isEnabled);
  const currentMatches = enabledItems.some((r) => cidrContains(r.cidr, data.currentIp));

  const onDelete = async (r: IpRuleDto) => {
    if (!(await confirm({ title: '許可ルールを削除しますか？', message: `${org.label} の許可ルール「${r.cidr}」を削除します。`, tone: 'danger', confirmLabel: '削除する' }))) return;
    try {
      await del.mutateAsync({ id: r.id });
      toast.success('許可ルールを削除しました');
    } catch (e) {
      const msg = selfLockoutMessage(e);
      if (msg && (await confirm({ title: 'あなた自身が利用できなくなります', message: msg, tone: 'danger', confirmLabel: '締め出されることを理解して削除' }))) {
        try {
          await del.mutateAsync({ id: r.id, confirmSelfLockout: true });
          toast.success('許可ルールを削除しました');
        } catch (e2) {
          toast.error('削除できませんでした', errorMessage(e2));
        }
        return;
      }
      if (!msg) toast.error('削除できませんでした', errorMessage(e));
    }
  };

  const columns: Column<IpRuleDto>[] = [
    { key: 'cidr', header: '許可する IP アドレス／CIDR', sortable: true, width: 280, render: (r) => <code className="bs-cidr">{r.cidr}</code> },
    { key: 'note', header: '備考', render: (r) => r.note || '—' },
    {
      key: 'isEnabled',
      header: '状態',
      width: 90,
      align: 'center',
      render: (r) => (r.isEnabled ? <StatusBadge tone="success" dot>有効</StatusBadge> : <StatusBadge dot>無効</StatusBadge>),
    },
    {
      key: 'match',
      header: '現在の接続元',
      width: 120,
      align: 'center',
      render: (r) => (cidrContains(r.cidr, data.currentIp) ? <StatusBadge tone="info">一致</StatusBadge> : <span className="bs-muted">—</span>),
    },
    { key: 'updatedAt', header: '更新日時', width: 160, render: (r) => <span className="bs-num">{formatDateTime(r.updatedAt)}</span> },
    {
      key: 'actions',
      header: '操作',
      width: 170,
      render: (r) => (
        <span className="bs-sec-actions">
          <Button size="sm" icon={<Pencil size={14} />} onClick={() => setEditing(r)}>
            編集
          </Button>
          <Button size="sm" variant="danger" icon={<Trash2 size={14} />} onClick={() => void onDelete(r)} aria-label={`${r.cidr} を削除`}>
            削除
          </Button>
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="システム管理"
        title="IPアドレス制限"
        description="組織ごとに、利用を許可する接続元の IP アドレス・CIDR を登録します。有効な許可ルールが 1 件以上ある組織だけが制限され、ルールの無い組織は制限されません。"
      />

      {data.restrictionDisabled && (
        <div className="bs-callout bs-callout--danger" role="alert">
          <AlertTriangle size={18} aria-hidden />
          <div>
            <strong>
              現在、IP アドレス制限は緊急解除中です（<code>IP_RESTRICTION_DISABLED=true</code>）。
            </strong>
            登録したルールは適用されていません。原因を解消したら、運用設計書の手順で解除を戻してください。
          </div>
        </div>
      )}

      <div className="bs-ip-current">
        <Network size={18} aria-hidden />
        <span>あなたの現在の接続元 IP アドレス</span>
        <code className="bs-cidr bs-cidr--lg" data-testid="current-ip">
          {data.currentIp ?? '不明'}
        </code>
        <span className="bs-muted">（基金のルールを変更するときは、この IP が許可範囲に含まれるようにしてください）</span>
      </div>

      <div className="bs-ip-layout">
        <nav className="bs-ip-orgs" aria-label="組織">
          <p className="bs-ip-orgs__caption">組織を選択</p>
          <ul>
            {orgs.map((o) => {
              const k = orgKey(o);
              const c = counts.get(k);
              return (
                <li key={k}>
                  <button type="button" className={cx('bs-ip-org', k === orgKey(org) && 'is-active')} onClick={() => setSelected(k)} aria-current={k === orgKey(org) ? 'true' : undefined}>
                    <span className="bs-ip-org__name">
                      {o.label}
                      {o.sub && <small>{o.sub}</small>}
                    </span>
                    {c && c.enabled > 0 ? <StatusBadge tone="accent">制限 {c.enabled}件</StatusBadge> : <span className="bs-ip-org__free">制限なし</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <Panel
          title={`${org.label} の許可ルール`}
          description={
            enabledItems.length === 0
              ? 'この組織には有効な許可ルールがありません（どのネットワークからでも利用できます）。'
              : `この組織のユーザーは、下の有効なルールに一致する接続元からのみ利用できます（${enabledItems.length}件）。`
          }
          actions={
            <Button variant="primary" icon={<Plus size={16} />} onClick={() => setEditing('new')}>
              許可 IP を追加
            </Button>
          }
        >
          {org.organizationType === '基金' && enabledItems.length > 0 && !currentMatches && (
            <div className="bs-callout bs-callout--warning">
              <AlertTriangle size={16} aria-hidden /> 現在の接続元（{data.currentIp ?? '不明'}）は基金の許可ルールに含まれていません。
            </div>
          )}
          <DataTable
            caption={`${org.label} の許可ルール`}
            columns={columns}
            rows={items}
            rowKey={(r) => r.id}
            dense
            rowClassName={(r) => (r.isEnabled ? undefined : 'is-muted')}
            empty={<EmptyState compact icon={<Network />} title="許可ルールはありません" description="「許可 IP を追加」で登録すると、この組織への IP アドレス制限が始まります。" />}
          />
        </Panel>
      </div>

      {editing && (
        <IpRuleDialog
          key={editing === 'new' ? 'new' : editing.id}
          org={org}
          target={editing === 'new' ? undefined : editing}
          currentIp={data.currentIp}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function IpRuleDialog({ org, target, currentIp, onClose }: { org: Org; target?: IpRuleDto; currentIp: string | null; onClose: () => void }) {
  const isNew = !target;
  const save = useSaveIpRule();
  const toast = useToast();
  const confirm = useConfirm();
  const {
    register,
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { cidr: target?.cidr ?? '', note: target?.note ?? '', isEnabled: target?.isEnabled ?? true } });

  const submit = async (v: Values, confirmations?: string[]) => {
    const input: IpRuleInput = {
      organizationType: org.organizationType,
      businessOperatorId: org.businessOperatorId,
      cidr: v.cidr,
      note: v.note || null,
      isEnabled: v.isEnabled,
      confirmations,
    };
    await save.mutateAsync({ id: target?.id ?? null, input });
    toast.success(isNew ? '許可ルールを登録しました' : '許可ルールを更新しました');
    onClose();
  };

  const onSubmit = handleSubmit(async (v) => {
    try {
      await submit(v);
    } catch (e) {
      const msg = selfLockoutMessage(e);
      if (msg) {
        if (await confirm({ title: 'あなた自身が利用できなくなります', message: msg, tone: 'danger', confirmLabel: '締め出されることを理解して保存' })) {
          try {
            await submit(v, ['selfLockout']);
          } catch (e2) {
            toast.error('保存できませんでした', errorMessage(e2));
          }
        }
        return;
      }
      if (!applyApiErrors(e, setError)) toast.error('保存できませんでした', errorMessage(e));
    }
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={isNew ? `許可 IP の追加（${org.label}）` : `許可 IP の編集（${org.label}）`}
      footer={
        <>
          <Button onClick={onClose}>キャンセル</Button>
          <Button variant="primary" icon={<Save size={16} />} loading={isSubmitting} onClick={() => void onSubmit()}>
            {isNew ? '登録する' : '保存する'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        <FormGrid columns={1}>
          <FormField
            label="IP アドレス／CIDR"
            required
            error={errors.cidr?.message}
            hint={
              <>
                単一の IP（203.0.113.10）または範囲（203.0.113.0/24、2001:db8::/48）。
                {currentIp && (
                  <button type="button" className="bs-linklike" onClick={() => setValue('cidr', currentIp, { shouldDirty: true })}>
                    現在の接続元（{currentIp}）を入力
                  </button>
                )}
              </>
            }
          >
            <TextField data-autofocus autoComplete="off" className="bs-num" {...register('cidr')} />
          </FormField>
          <FormField label="備考" error={errors.note?.message} hint="拠点名・回線名など（例：本所 事務室の光回線）">
            <TextField {...register('note')} />
          </FormField>
          <FormField label="状態">
            <Controller control={control} name="isEnabled" render={({ field }) => <Checkbox label="このルールを有効にする" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} />
          </FormField>
        </FormGrid>
      </form>
    </Modal>
  );
}
